"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { Send, Loader2, MessageSquare, User } from "lucide-react";
import { PLATFORM_DISPLAY } from "@/lib/platform";
import type { Message } from "@/lib/messages/types";

const POLL_INTERVAL_MS = 10000; // Poll for new messages every 10 seconds

type PartyType = "seller" | "buyer";

interface DealChatProps {
  dealId: string;
  dealTitle: string;
  currentUserId: string;
  /** For admin view: show messages from all parties */
  isAdmin?: boolean;
  /** Party information for admin view */
  parties?: {
    seller: { id: string; name: string };
    buyer: { id: string; name: string };
  };
}

/**
 * Inline chat component for deal messaging.
 * Shows message history and allows sending new messages.
 *
 * For admin view: Shows a toggle to switch between seller and buyer conversations.
 * Each conversation is separate - admin talks to seller OR buyer, not both at once.
 */
export function DealChat({
  dealId,
  dealTitle: _dealTitle,
  currentUserId,
  isAdmin = false,
  parties,
}: DealChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // For admin: which party's conversation to view
  const [selectedParty, setSelectedParty] = useState<PartyType>("seller");
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Get the currently selected party's ID
  const selectedPartyId =
    isAdmin && parties ? (selectedParty === "seller" ? parties.seller.id : parties.buyer.id) : null;

  // Filter messages for admin view to only show conversation with selected party
  const filteredMessages = useMemo(() => {
    if (!isAdmin || !parties) {
      return messages;
    }
    // In admin view, show only messages where the selected party is involved
    // (either as sender or recipient)
    return messages.filter(
      (m) => m.sender_id === selectedPartyId || m.recipient_id === selectedPartyId
    );
  }, [messages, isAdmin, parties, selectedPartyId]);

  // Count messages for each party (for admin badge)
  const messageCountsByParty = useMemo(() => {
    if (!isAdmin || !parties) {
      return { seller: 0, buyer: 0 };
    }
    return {
      seller: messages.filter(
        (m) => m.sender_id === parties.seller.id || m.recipient_id === parties.seller.id
      ).length,
      buyer: messages.filter(
        (m) => m.sender_id === parties.buyer.id || m.recipient_id === parties.buyer.id
      ).length,
    };
  }, [messages, isAdmin, parties]);

  // Single endpoint for both admin and user
  const endpoint = `/api/deals/${dealId}/messages`;

  // Fetch messages on mount and after sending
  const fetchMessages = async () => {
    try {
      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error("Failed to fetch messages");
      }

      const data = await response.json();
      setMessages(data.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Nachrichten konnten nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch messages on mount and poll for updates
  useEffect(() => {
    fetchMessages();

    // Set up polling interval for new messages
    const pollInterval = setInterval(() => {
      fetchMessages();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Scroll to bottom when filtered messages change or when switching parties
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [filteredMessages, selectedParty]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim()) return;

    setIsSending(true);
    setError(null);

    try {
      const body =
        isAdmin && selectedPartyId
          ? { recipient_id: selectedPartyId, body: newMessage.trim() }
          : { body: newMessage.trim() };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Nachricht konnte nicht gesendet werden");
      }

      setNewMessage("");
      // Refresh messages
      await fetchMessages();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffDays === 1) {
      return `Gestern, ${date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
    } else if (diffDays < 7) {
      return `${date.toLocaleDateString("de-DE", { weekday: "short" })}, ${date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
    } else {
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  // Check if message was sent by the current user
  const isOwnMessage = (message: Message) => {
    return message.sender_id === currentUserId;
  };

  const getPartyLabel = () => {
    if (!parties) return "";
    return selectedParty === "seller"
      ? `Verkäufer (${parties.seller.name})`
      : `Käufer (${parties.buyer.name})`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden">
      {/* Header with party toggle for admin */}
      {isAdmin && parties ? (
        <div className="border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-3 p-4 pb-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900">Nachrichten</h3>
              <p className="text-sm text-zinc-500">Wählen Sie eine Konversation</p>
            </div>
          </div>

          {/* Party toggle tabs */}
          <div className="flex px-4 pb-0">
            <button
              onClick={() => setSelectedParty("seller")}
              className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                selectedParty === "seller"
                  ? "border-primary text-primary"
                  : "border-transparent text-zinc-500 hover:text-zinc-700"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <User className="w-4 h-4" />
                Verkäufer
                {messageCountsByParty.seller > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-zinc-200 text-zinc-700 rounded-full">
                    {messageCountsByParty.seller}
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5 truncate">{parties.seller.name}</div>
            </button>
            <button
              onClick={() => setSelectedParty("buyer")}
              className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                selectedParty === "buyer"
                  ? "border-primary text-primary"
                  : "border-transparent text-zinc-500 hover:text-zinc-700"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <User className="w-4 h-4" />
                Käufer
                {messageCountsByParty.buyer > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-zinc-200 text-zinc-700 rounded-full">
                    {messageCountsByParty.buyer}
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5 truncate">{parties.buyer.name}</div>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-4 border-b border-zinc-200 bg-zinc-50">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">Kommunikation</h3>
            <p className="text-sm text-zinc-500">Mit {PLATFORM_DISPLAY.name}</p>
          </div>
        </div>
      )}

      {/* Messages area */}
      <div ref={messagesContainerRef} className="h-64 overflow-y-auto p-4 space-y-4 bg-zinc-50/50">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center mb-3">
              <MessageSquare className="w-6 h-6 text-zinc-400" />
            </div>
            <p className="text-sm text-zinc-500">
              {isAdmin
                ? `Noch keine Nachrichten mit ${getPartyLabel()}`
                : "Starten Sie eine Konversation mit unserem Team"}
            </p>
          </div>
        ) : (
          <>
            {filteredMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${isOwnMessage(message) ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] ${
                    isOwnMessage(message)
                      ? "bg-primary text-white rounded-2xl rounded-br-md"
                      : "bg-primary border border-zinc-200 rounded-2xl rounded-bl-md"
                  }`}
                >
                  {/* Sender label */}
                  <div className={`px-4 pt-3 pb-1 text-xs font-medium text-white/50`}>
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      {isOwnMessage(message)
                        ? isAdmin
                          ? PLATFORM_DISPLAY.name
                          : "Sie"
                        : isAdmin
                          ? getPartyLabel()
                          : PLATFORM_DISPLAY.name}
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <p className={`text-sm whitespace-pre-wrap text-white`}>{message.body}</p>
                  </div>
                  <div className={`px-4 pb-3 text-xs text-white/60`}>
                    {formatTime(message.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Compose area */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-zinc-200">
        {isAdmin && parties && (
          <div className="mb-2 text-xs text-zinc-500">
            Nachricht an: <span className="font-medium text-zinc-700">{getPartyLabel()}</span>
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={
              isAdmin
                ? `Nachricht an ${selectedParty === "seller" ? "Verkäufer" : "Käufer"} schreiben...`
                : "Nachricht schreiben..."
            }
            className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={isSending || !newMessage.trim()}
            className="px-4 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
